import React, { useEffect } from 'react'
import { BACKEND_APIS, COMMON_FILTERS_COMPONENTS, DATE_PRESETS, DATE_PRESETS_AFTER, SERVICES_TYPE } from '@constants'
import { disabledBefore } from '@ui-seeker/core'
import { bookingRange, dateRange, mainSite, sites, offices, isOfficeGroup, officeGroup, isSiteGroup, siteGroup, mainSiteProvider } from '@signals'
import { getNextDate } from '@ui-seeker/utils'
import { batch } from '@preact/signals-react'
import AppCommonFilters from '@components/NewCommonFilters'
import LeadingAnalyzerFiltersFooterActions from 'Pages/BusinessLogistics/LeadingAnalyzer/Page/Filters/Footer'
import { useValidatorSeeker } from '@app'
import { areCommonFiltersValid } from '@signals/commonFilters/filters'
import { formData } from 'Pages/BusinessLogistics/LeadingAnalyzer/Page/@signals'
import { leadingSchema } from 'Pages/BusinessLogistics/LeadingAnalyzer/Page/schema'

export default function LeadingAnalyzerFilters() {

	const { errors, isValid } = useValidatorSeeker(formData.value, leadingSchema)

	useEffect(() => {
		areCommonFiltersValid.value = isValid
	}, [JSON.stringify(errors)])

	return <AppCommonFilters
		headerComponents={[
			{
				name: COMMON_FILTERS_COMPONENTS.DAILY_PRICE_TOGGLE,
				grid: { xs: 1.5 },
				error: errors?.isDailyPrice,
			}, {
				name: COMMON_FILTERS_COMPONENTS.IS_OFFICE_GROUP,
				grid: { xs: 1.5 },
			}, {
				name: COMMON_FILTERS_COMPONENTS.IS_SITE_GROUP,
				grid: { xs: 1.5 },
			}, {
				name: COMMON_FILTERS_COMPONENTS.MAIN_SITE_PROVIDER_TOGGLE,
				grid: { xs: 1.5 },
			},
		]}
		components={[!isOfficeGroup.value && {
			name: COMMON_FILTERS_COMPONENTS.OFFICE_PICKUP,
			label: 'Pick up Office',
			multiple: true,
			grid: { xs: 3 },
			filters: {
				service_type: SERVICES_TYPE.BI,
				id_office_pickup: offices.value,
			},
			error: errors?.officePickupID
		}, isOfficeGroup.value && {
			name: COMMON_FILTERS_COMPONENTS.OFFICE_GROUP,
			label: 'Pickup office Group',
			multiple: true,
			grid: { xs: 3 },
			filters: {
				service_type: SERVICES_TYPE.BI,
				officeGroupID: officeGroup.value,
			},
			error: errors?.officeGroupID,
		}, {
			name: COMMON_FILTERS_COMPONENTS.OFFICE_DROPOFF,
			label: 'Drop off Office',
			multiple: false,
			grid: { xs: 3 },
			filters: {
				service_type: SERVICES_TYPE.BI,
			},
			error: errors?.officeDropoffID
		}, !mainSiteProvider.value && {
			name: COMMON_FILTERS_COMPONENTS.MAIN_SITE,
			grid: { xs: 3 },
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/site`,
			multiple: false,
			filters: {
				service_type: SERVICES_TYPE.BI,
				mainSiteID: mainSite.value,
				siteID: sites.value
			},
			error: errors?.siteMainID
		}, mainSiteProvider.value && {
			name: COMMON_FILTERS_COMPONENTS.MAIN_PROVIDER,
			grid: { xs: 3 },
			filters: {
				service_type: SERVICES_TYPE.BI,
				siteID: sites.value,
			},
            urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/provider`,
			error: errors?.providerMainID
		}, !isSiteGroup.value && {
			name: COMMON_FILTERS_COMPONENTS.SITE,
			grid: { xs: 3 },
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/site`,
			filters: {
				service_type: SERVICES_TYPE.BI,
				mainSiteID: mainSite.value,
				siteID: sites.value
			},
			error: errors?.siteID
		}, isSiteGroup.value && {
			name: COMMON_FILTERS_COMPONENTS.SITE_GROUP,
			label: 'Site Group',
			multiple: true,
			grid: { xs: 3 },
			filters: {
				service_type: SERVICES_TYPE.BI,
				siteGroupID: siteGroup.value,
			},
			error: errors?.siteGroupID,
		}, {
			name: COMMON_FILTERS_COMPONENTS.BOOKING_DATE,
			grid: { xs: 2 },
			value: bookingRange.value,
			onChange: e => batch(() => {
				bookingRange.value = e
				dateRange.value = [
					getNextDate({ days: 0 }, e[0]),
					getNextDate({ days: 0 }, e[1])
				]
			}),
			ranges: DATE_PRESETS,
			label: 'Booking date range',
			error: errors?.startBookingDate
		}, {
			name: COMMON_FILTERS_COMPONENTS.DATE_RANGE,
			grid: { xs: 2 },
			ranges: DATE_PRESETS_AFTER,
			label: 'Pickup date range',
			shouldDisableDate: disabledBefore(bookingRange.value[0]),
			error: errors?.startPickupDate,
		}, {
			name: COMMON_FILTERS_COMPONENTS.GROUP_BY,
			grid: { xs: 2 },
			disableClearable: true,
			error: errors?.groupBy
		}, {
			name: COMMON_FILTERS_COMPONENTS.CATEGORIES,
			grid: { xs: 3 },
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/category`,
			filters: {
				siteID: sites.value,
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.categoryID
		}, {
			name: COMMON_FILTERS_COMPONENTS.RENTAL_LENGTH,
			multiple: true,
			grid: { xs: 1 },
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/lor`,
			onAjax: options => options.map(option => ({
				label: `${option.label}`,
				value: `${option.value}`
			})),
			filters: {
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.lor
		}, {
			name: COMMON_FILTERS_COMPONENTS.TIERS,
			grid: { xs: 2 },
			filters: {
				siteID: sites.value,
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.tiers
		}, {
			name: COMMON_FILTERS_COMPONENTS.PROVIDERS,
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/provider`,
			filters: {
				siteID: sites.value,
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.providerID
		}, {
			name: COMMON_FILTERS_COMPONENTS.CURRENCY,
			grid: { xs: 1 },
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/currencies`,
			filters: {
				siteID: sites.value,
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.currency
		}]}
		featuresComponents={[{
			name: COMMON_FILTERS_COMPONENTS.ACRISS,
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/acriss`,
			filters: {
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.acriss
		}, {
			name: COMMON_FILTERS_COMPONENTS.MILEAGE,
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/mileage`,
			filters: {
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.unlimited_km
		}, {
			name: COMMON_FILTERS_COMPONENTS.TRANSMISSION,
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/transmission`,
			filters: {
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.automatic
		}, {
			name: COMMON_FILTERS_COMPONENTS.FUEL_POLICY,
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/fuel`,
			filters: {
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.fuelPolicyID
		}, {
			name: COMMON_FILTERS_COMPONENTS.INSURANCE,
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/insurance`,
			filters: {
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.insurance
		}, {
			name: COMMON_FILTERS_COMPONENTS.PREPAID,
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/payment_method`,
			filters: {
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.is_prepaid
		}, {
			name: COMMON_FILTERS_COMPONENTS.IN_TERMINAL,
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/terminal`,
			filters: {
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.in_terminal_pickup
		}, {
			name: COMMON_FILTERS_COMPONENTS.WEB_SUPPLIERS,
			urlAjax: `${BACKEND_APIS.MASTER_DATA}/options/web_suppliers`,
			filters: {
				service_type: SERVICES_TYPE.BI
			},
			error: errors?.webSuppliers
		}]}
		Footer={LeadingAnalyzerFiltersFooterActions}
	/>
}